import { flattenDeep, getOr, isNull, isUndefined } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { AgencyDataset, AgencyDatasetField, AgencyDefinition, AgencyLinkedSingleField, DatasetDefinition, DatasetDefinitionDB, DatasetField, DatasetSection, DocumentSpecificHiddenFields, FormDatasetDefinition, HiddenField, HiddenSection, TableDatasetDefinition } from '../../../datasets/store/types';
import { DatasetsOpenFieldSection, DatasetView, DocumentDatasetModal, DocumentNameDatasets, DocumentSpecificSectionIds, LinkDirection, SectionOpen, SelectedDataset, SettingsModal } from './types';
import { validateAllFields, validateFieldLabels, validateSectionFieldLabels, validateSectionFields } from './utils';
import { isFormDatasetDefinition } from '../../../datasets/store/typeAssertions';
import { getDatasetRoot } from '../../store/selectors';

export const getModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).modalOpen;

export const getDatasetDefinition = (state: ApplicationState): DatasetDefinition | null => getDatasetRoot(state).datasetDefinition;

export const getPreviousDatasetIds = (state: ApplicationState): number[] => getDatasetRoot(state).previousDatasetIds;

export const getAllDatasetDefinitions = (state: ApplicationState): DatasetDefinitionDB[] => getDatasetRoot(state).publishedDatasetDefinitions;

export const getDefinitionsForDataset = (state: ApplicationState): DatasetDefinition[] => getDatasetRoot(state).datasetDefinitions;

export const getPreviousDatasetDefinitions = (state: ApplicationState): { datasetTitle: string; datasetId: number; }[] => {
    const previousDatasetIds = getPreviousDatasetIds(state);
    const allDatasetDefinitions = getAllDatasetDefinitions(state);
    return previousDatasetIds.map(id => {
        const { datasetId, datasetTitle } = allDatasetDefinitions.find(({ datasetId }) => datasetId === id)!;
        return { datasetId, datasetTitle };
    });
};

export const getSectionModalOpen = (state: ApplicationState): SectionOpen | null => getDatasetRoot(state).sectionModalOpen;

export const getSelectedSection = (state: ApplicationState): DatasetSection | null => getDatasetRoot(state).selectedSection;

export const getSections = (state: ApplicationState): DatasetSection[] | null => getDatasetRoot(state).datasetDefinition ? (getDatasetRoot(state).datasetDefinition as FormDatasetDefinition)!.datasetSections : null;

export const getDatasetPreviewOpen = (state: ApplicationState): boolean => getDatasetRoot(state).previewOpen;

export const getDefinitionHistoryModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).definitionHistoryModalOpen;

export const getFormDatasetFields = (state: ApplicationState): DatasetField[] => {
    const selectedSection = getSelectedSection(state);
    const datasetDefinition = getDatasetDefinition(state);
    if (selectedSection && datasetDefinition) {
        return (getDatasetDefinition(state) as FormDatasetDefinition).datasetFields[selectedSection.id] || [];
    } else {
        return [];
    }
};

export const getTableDatasetFields = (state: ApplicationState): DatasetField[] => {
    const datasetDefinition = getDatasetDefinition(state);
    if (datasetDefinition) {
        return (getDatasetRoot(state).datasetDefinition as TableDatasetDefinition)!.datasetFields;
    } else {
        return [];
    }
};

export const getDefinitionHasUnlabelledFields = (state: ApplicationState): boolean => {
    const datasetDefinition = getDatasetDefinition(state);
    if (!isNull(datasetDefinition)) {
        return !validateFieldLabels(datasetDefinition);
    }
    return false;
};

export const getDefinitionHasIncompleteFields = (state: ApplicationState): boolean => {
    const datasetDefinition = getDatasetDefinition(state);
    if (!isNull(datasetDefinition)) {
        return !validateAllFields(datasetDefinition);
    }
    return false;
};

export const getSectionHasIncompleteFields = (sectionId: string) => (state: ApplicationState): boolean => {
    const datasetDefinition = getDatasetDefinition(state);
    if (!isNull(datasetDefinition) && isFormDatasetDefinition(datasetDefinition)) {
        return !validateSectionFields(datasetDefinition, sectionId) || !validateSectionFieldLabels(datasetDefinition, sectionId);
    }
    return false;
};

export const getSelectedGroup = (state: ApplicationState): string | null => getDatasetRoot(state).selectedGroup;

export const getPublishModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).publishModalOpen;

export const getPublishReason = (state: ApplicationState): string => getDatasetRoot(state).datasetDefinition?.publishReason || '';

export const getPublishError = (state: ApplicationState): string | null => getDatasetRoot(state).publishError;

export const getIsPublishing = (state: ApplicationState): boolean => getDatasetRoot(state).isPublishing;

export const getHelpModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).helpModalOpen;

export const getSettingsLinkModalOpen = (state: ApplicationState): SettingsModal => getDatasetRoot(state).settingsLinkModalOpen;

export const getDatasetView = (state: ApplicationState): DatasetView => getDatasetRoot(state).datasetView;

export const getConfirmDeleteDataset = (state: ApplicationState): DatasetDefinitionDB | DatasetDefinition | null => getDatasetRoot(state).confirmDeleteDataset;

export const getIsDeleting = (state: ApplicationState): boolean => getDatasetRoot(state).isDeleting;

export const getDefinitionClientIds = (state: ApplicationState): number[] => getDatasetRoot(state).definitionClientIds;

export const getNewDatasetFields = (state: ApplicationState): (HiddenField | HiddenSection)[] => getDatasetRoot(state).newFields;

export const getReorderSectionsModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).reorderSectionsModalOpen;

export const getCalculatorHelpModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).calculatorHelpModalOpen;

export const getRiskToleranceHelpModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).riskToleranceHelpModalOpen;

export const getUnsavedChangesModalStatus = (state: ApplicationState): LinkDirection | null => getDatasetRoot(state).unsavedChangesModalOpen;

export const getLinkUpUnsavedChangesModalOpen = (state: ApplicationState): boolean => getUnsavedChangesModalStatus(state) === LinkDirection.UP;
export const getLinkDownUnsavedChangesModalOpen = (state: ApplicationState): boolean => getUnsavedChangesModalStatus(state) === LinkDirection.DOWN;

export const getDefinitionHasUpdated = (state: ApplicationState): boolean => getDatasetRoot(state).datasetDefinitionUpdated;

export const getAllDatasets = (state: ApplicationState): DocumentNameDatasets[] => getDatasetRoot(state).documentNameDatasets;

export const isFetchingDatasets = (state: ApplicationState): boolean => getDatasetRoot(state).fetchingDocumentNameDatasets;

export const getDatasetBuilderPathname = (state: ApplicationState): DatasetView => {
    const url = state.router.location?.pathname || '';
    const showAgencyDatasets = getDatasetView(state) === DatasetView.TABLE_AGENCY;
    if (url && url.includes('/configure')) {
        return DatasetView.DOCUMENTS_CONFIGURE;
    }
    if (url && url.includes('/preview')) {
        return DatasetView.DOCUMENTS_PREVIEW;
    }
    if (url && url.includes('/documents')) {
        return DatasetView.DOCUMENTS_LIST;
    }
    return showAgencyDatasets ? DatasetView.TABLE_AGENCY : DatasetView.TABLE_STANDARD;
};

export const getCurrentHiddenDocumentFields = (state: ApplicationState): DocumentSpecificHiddenFields => getDatasetRoot(state).currentHiddenDocumentFields;

export const getSavedHiddenDocumentFields = (state: ApplicationState): DocumentSpecificHiddenFields => getDatasetRoot(state).savedHiddenDocumentFields;

export const getOpenFieldsAndSections = (state: ApplicationState): DatasetsOpenFieldSection[] => getDatasetRoot(state).openFieldsAndSections;

export const getDocumentDatasetModal = (state: ApplicationState): DocumentDatasetModal | null => getDatasetRoot(state).documentDatasetModal;

export const getUnsavedHiddenDocumentChangesModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).unsavedHiddenDocumentChangesModalOpen;

export const getPreviewDatasetSelectedDocument = (state: ApplicationState): number | null => getDatasetRoot(state).previewDatasetSelectedDocument;

export const getIsSavingHiddenFields = (state: ApplicationState): boolean => getDatasetRoot(state).isSavingDocumentDatasetHiddenFields;

export const getCurrentDocumentSpecificSectionIds = (state: ApplicationState): DocumentSpecificSectionIds => getDatasetRoot(state).currentDatasetSectionReferences;

export const getSavedDocumentSpecificSectionIds = (state: ApplicationState): DocumentSpecificSectionIds => getDatasetRoot(state).savedDatasetSectionReferences;

export const getCurrentDocumentSpecificSectionReferences = (datasetId: number, documentNameId: number, sectionLabel: string) => (state: ApplicationState): string[] => {
    const currentReferences = getDatasetRoot(state).currentDatasetSectionReferences[datasetId];
    if (!isUndefined(currentReferences)) {
        return currentReferences[documentNameId].find(({ sectionId }) => sectionId === sectionLabel)?.references || [];
    }
    return [];
};

export const getSectionHasSpecificReferences = (datasetId: number, documentNameIds: number[], sectionLabel: string) => (state: ApplicationState): boolean => {
    let documentReferences: boolean[] = [];
    documentNameIds.map(documentNameId => {
        const datasetReferences = getDatasetRoot(state).currentDatasetSectionReferences[datasetId];
        let hasRef = false;
        if (!isUndefined(datasetReferences)) {
            hasRef = !!datasetReferences[documentNameId].find(({ sectionId }) => sectionId === sectionLabel)?.references || false;
        }
        documentReferences.push(hasRef);
    });
    return documentReferences.some(ref => !!ref);
};

export const getDatasetDocumentConfigUpdated = (state: ApplicationState): boolean => getDatasetRoot(state).datasetDocumentConfigUpdated;

export const getConfiguredDatasetParentId = (state: ApplicationState): number | null => getDatasetRoot(state).configureDatasetParentId;

// Agency Builder selectors

export const getAgencyModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).agencyModalOpen;

export const getAgencyDefinition = (state: ApplicationState): AgencyDefinition | null => getDatasetRoot(state).agencyDefinition;

export const getAllAgencyDefinitions = (state: ApplicationState): AgencyDataset[] => getDatasetRoot(state).agencyDefinitions;

export const getAgencyDatasetFields = (state: ApplicationState): AgencyDatasetField[] => getOr([], 'datasetFields', getAgencyDefinition(state));

export const getAgencySettingsModalOpen = (state: ApplicationState): number | null => getDatasetRoot(state).agencySettingsModalOpen;

export const getAvailableAgencyFields = (state: ApplicationState): AgencyLinkedSingleField[] => {
    const agencyDefinition = getAgencyDefinition(state);
    const allAgencyFields = getDatasetRoot(state).availableAgencyFields;
    if (!agencyDefinition) {
        return allAgencyFields;
    }
    const linkedAgencyFields = flattenDeep(agencyDefinition.datasetFields.map(({ linkedFields }) => linkedFields));
    return allAgencyFields.filter(({ id }) => !linkedAgencyFields.map(linked => linked.id).includes(id));
};

export const getPublishConfirmationModalOpen = (state: ApplicationState): boolean => getDatasetRoot(state).publishConfirmationModalOpen;

export const getSelectedDatasetId = (state: ApplicationState): SelectedDataset | null => getDatasetRoot(state).selectedDataset;
